import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ContourSwatch } from "./TrailProfile";

const LINES = {
  zone1: "You've named the terrain you've been walking. That noticing is the first softening.",
  zone2: "You surveyed the landscape. Nothing to fix here — only to see more clearly.",
  zone3: "You found new paths through the switchbacks. Changing direction is not losing the way.",
  zone4: "From the ridgeline, the view is wider. You're reading your own map now.",
  zone5: "You crossed the exposed face. Honest weather, steady feet. Well walked.",
  zone6: "The high meadow opens. There's room to breathe here — take some.",
  zone7: "You arrived, and began the gentle way down. Rhythm, not endurance.",
};

export const Waypoint = ({ zone, prevGap, onClose, onJournal, onPause, reducedMotion, showBringToSession, onBringToSession }) => {
  return (
    <AnimatePresence>
      {zone && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0.2 : 0.8 }}
          className="fixed inset-0 z-[52] flex items-center justify-center bg-ivory-100/95 px-6 backdrop-blur-md"
          data-testid="waypoint-screen"
        >
          <div className="w-full max-w-md text-center">
            <p className="kicker text-gold-deep">Waypoint</p>
            <h2 className="mt-2 font-display text-3xl text-ink">{zone.name}</h2>
            <div className="mt-6 flex items-center justify-center gap-6 text-ink-soft">
              <div className="text-center">
                <ContourSwatch gap={prevGap || 6} size={64} className="mx-auto" />
                <p className="mt-1 text-[11px] uppercase tracking-editorial text-ink-muted">Before</p>
              </div>
              <span className="text-gold-deep">→</span>
              <div className="text-center">
                <ContourSwatch gap={zone.gap} size={64} className="mx-auto text-gold-deep" />
                <p className="mt-1 text-[11px] uppercase tracking-editorial text-gold-deep">Now — looser</p>
              </div>
            </div>
            <p className="mx-auto mt-6 max-w-sm font-serif text-lg leading-relaxed text-ink-soft">{LINES[zone.id] || "A quiet place to pause before you continue."}</p>
            <p className="mt-2 text-sm text-ink-muted">An invitation, never a requirement.</p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <button type="button" onClick={onPause} data-testid="waypoint-pause" className="rounded-full px-5 py-2.5 text-sm text-ink-soft underline-offset-4 hover:text-gold-deep hover:underline">Take a pause</button>
              <button type="button" onClick={onJournal} data-testid="waypoint-journal" className="rounded-full px-5 py-2.5 text-sm text-ink-soft underline-offset-4 hover:text-gold-deep hover:underline">Write a note</button>
              <button type="button" onClick={onClose} data-testid="waypoint-continue" className="debossed-btn rounded-full px-7 py-3 font-display text-lg text-ink">Continue</button>
            </div>
            {showBringToSession && (
              <button type="button" onClick={onBringToSession} data-testid="waypoint-bring-to-session" className="mx-auto mt-4 block text-sm text-gold-deep underline-offset-4 hover:underline">
                Bring this to a session →
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const PausePractice = ({ pause, onClose }) => (
  <AnimatePresence>
    {pause && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[58] flex items-center justify-center bg-ink/60 px-6 backdrop-blur-md" onClick={onClose} data-testid="pause-screen">
        <div className="w-full max-w-sm rounded-2xl bg-ivory-100 p-7 text-center" onClick={(e) => e.stopPropagation()}>
          <p className="kicker text-gold-deep">A pause · under a minute</p>
          <h3 className="mt-2 font-display text-2xl text-ink">{pause.title}</h3>
          <p className="mt-3 font-serif text-lg leading-relaxed text-ink-soft">{pause.text}</p>
          <button type="button" onClick={onClose} data-testid="pause-done" className="debossed-btn mt-6 rounded-full px-6 py-2.5 text-ink">I'm ready</button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
